import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanBarcode, Search } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";

export function BarcodeScannerModal({ open, onClose, onScan }) {
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(false);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    Html5Qrcode.getCameras().then((cameras) => {
      setCameraAvailable(cameras && cameras.length > 0);
    }).catch(() => setCameraAvailable(false));
  }, []);

  useEffect(() => {
    if (!open) {
      stopScanner();
      setManualCode("");
      setScanning(false);
    }
  }, [open]);

  const startScanner = async () => {
    try {
      if (scannerRef.current) await scannerRef.current.stop().catch(() => {});
      const scanner = new Html5Qrcode("barcode-reader");
      scannerRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 280, height: 120 } },
        (decodedText) => {
          stopScanner();
          onScan(decodedText);
          onClose(false);
        },
        () => {},
      );
    } catch (err) {
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      onScan(manualCode.trim());
      onClose(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopScanner(); onClose(o); }}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" aria-describedby="barcode-desc">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-[#0F172A] flex items-center gap-2">
            <ScanBarcode size={18} /> Scan Part Barcode
          </DialogTitle>
        </DialogHeader>
        <p id="barcode-desc" className="text-xs text-[#64748B]">
          Scan a barcode or enter the SAP/part number manually
        </p>

        {cameraAvailable && (
          <div className="space-y-3">
            <div id="barcode-reader" ref={containerRef} className="w-full bg-[#0F172A] min-h-[200px]" data-testid="barcode-reader-area" />
            {!scanning ? (
              <Button
                onClick={startScanner}
                className="w-full bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none"
                data-testid="start-camera-button"
              >
                <ScanBarcode size={14} className="mr-2" /> Start Camera Scanner
              </Button>
            ) : (
              <Button
                onClick={stopScanner}
                variant="outline"
                className="w-full rounded-none border-[#DC2626] text-[#DC2626]"
                data-testid="stop-camera-button"
              >
                Stop Camera
              </Button>
            )}
          </div>
        )}

        <div className={cameraAvailable ? "border-t border-[#E2E8F0] pt-4" : ""}>
          <form onSubmit={handleManualSubmit} className="flex gap-2">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Enter SAP # or part number..."
              className="flex-1 rounded-none border-[#E2E8F0] font-mono"
              autoFocus={!cameraAvailable}
              data-testid="barcode-manual-input"
            />
            <Button
              type="submit"
              disabled={!manualCode.trim()}
              className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none"
              data-testid="barcode-manual-submit"
            >
              <Search size={14} />
            </Button>
          </form>
          <p className="text-xs text-[#94A3B8] mt-2">
            Searches SAP #, manufacturer part #, and vendor part #
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
